import { Box, Stack } from "@mui/material";
import { useTranslation } from "next-i18next";
import { WfHeading } from "src/components-mui/base/wf-heading/wf-heading";
import { ArrowButton } from "src/components-mui/common/buttons/arrow-button/arrow-button";
import { ModulePrimaryHeadline } from "src/components-mui/common/module-headlines";
import { SimpleRichText } from "src/components-mui/common/rich-text/simple-rich-text";
import { Container } from "src/components-mui/layout/container";
import { IWikifolioInfoCardProps, WikifolioInfoCard } from "src/components-mui/modules/components/wikifolio-info-card/wikifolio-info-card";
import { ISelectedWikifolioData } from "src/components-mui/modules/selected-wikifolios/selected-wikifolios-module.types";
import { SCROLL_DIRECTION, useAutoScrollingList } from "src/hooks/dom/use-auto-scrolling-list";

const CARD_WIDTH_SM = 324;
const CARD_WIDTH_MD = 450;
const CARD_WIDTH_LG = "358px";
const LAST_CARD_WIDTH_OFFSET = 6;

const MAX_COLUMNS = 3;

const colorSchemeMap = {
    white: {
        bgcolor: "white",
    },
    gray: {
        bgcolor: "gray.50",
    },
};

export interface ISelectedWikifolioColumn {
    title?: string;
    wikifolios: ISelectedWikifolioData[];
}

export interface ISelectedWikifoliosModuleProps {
    title: string;
    wikifolioColumns: ISelectedWikifolioColumn[];
    description?: string;
    colorScheme?: keyof typeof colorSchemeMap;
    wfCardProps?: Partial<IWikifolioInfoCardProps>;
}

export const SelectedWikifoliosModule = ({
    title,
    description,
    wikifolioColumns,
    colorScheme = "white",
    wfCardProps,
}: ISelectedWikifoliosModuleProps) => {
    const hasCardsWithTitle = wikifolioColumns.some(column => Boolean(column.title));
    const { t } = useTranslation("common");

    const { canScrollPrev, canScrollNext, scrollContainerRef, collectItemRefs, handleScroll } = useAutoScrollingList();

    const hasNavButtonsLg = wikifolioColumns.length > MAX_COLUMNS;
    const isBgWhite = colorScheme === "white";
    const mbHeading = description ? 3 : 5;

    return (
        <Box sx={colorSchemeMap[colorScheme]}>
            <Container sx={{ py: !isBgWhite ? [6, 6, 8] : undefined }}>
                <ModulePrimaryHeadline sx={{ mb: mbHeading }}>{title}</ModulePrimaryHeadline>
                {description && <SimpleRichText text={description} sx={{ mb: [3, 5] }} fontSize="lg" />}
                <Stack
                    ref={scrollContainerRef}
                    direction={{ xs: "column", sm: "row" }}
                    spacing={{ xs: hasCardsWithTitle ? 5 : 1, sm: 3 }}
                    sx={{
                        scrollbarWidth: "none",
                        scrollSnapType: "x mandatory",
                        scrollBehavior: "smooth",
                        "&::-webkit-scrollbar": { display: "none" },
                        overflowX: ["visible", "auto", "auto", hasNavButtonsLg ? "auto" : "visible"],
                        pl: [0, 5, 3, hasNavButtonsLg ? 2 : 0],
                        pr: [0, 2, 2, hasNavButtonsLg ? 2 : 0],
                        ml: [0, -5, -3, hasNavButtonsLg ? -2 : 0],
                        mr: [0, -2, -2, hasNavButtonsLg ? -2 : 0],
                        py: 5,
                        my: -5,
                    }}
                >
                    {wikifolioColumns.map((column, index) => {
                        // Dev-Note: we need the adjustments for the last card because else there would be overflowing bugs in Safari
                        const isLastCard = index === wikifolioColumns.length - 1;
                        const isFirstCard = index === 0;
                        const lastCardOffset = isLastCard ? LAST_CARD_WIDTH_OFFSET : 0;

                        return (
                            <Stack
                                data-index={index}
                                ref={node => collectItemRefs({ node, index })}
                                key={index}
                                spacing={2}
                                sx={{
                                    alignItems: "center",
                                    width: ["100%", CARD_WIDTH_SM + lastCardOffset, CARD_WIDTH_MD + lastCardOffset, CARD_WIDTH_LG],
                                    pr: [0, isFirstCard ? 0 : 2, isLastCard ? 2 : 0, 0],
                                    flexShrink: 0,
                                }}
                            >
                                {column.title && (
                                    <WfHeading component="h2" fontSize="xl" noWrap sx={{ width: "100%", wordBreak: "break-all" }}>
                                        {column.title}
                                    </WfHeading>
                                )}
                                <Stack spacing={1} sx={{ alignItems: "center", width: "100%" }}>
                                    {column.wikifolios.map(wikifolio => {
                                        const { trader, ranking, symbol, shortDescription } = wikifolio;
                                        return (
                                            <WikifolioInfoCard
                                                key={wikifolio.symbol}
                                                trader={trader}
                                                ranking={ranking}
                                                symbol={symbol}
                                                shortDescription={shortDescription}
                                                sx={{ width: "100%" }}
                                                {...wfCardProps}
                                                hidePoints
                                            />
                                        );
                                    })}
                                </Stack>
                            </Stack>
                        );
                    })}
                </Stack>
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                        mt: 3,
                        alignItems: "center",
                        justifyContent: "flex-end",
                        display: ["none", "flex", "flex", hasNavButtonsLg ? "flex" : "none"],
                    }}
                >
                    <ArrowButton
                        sx={{ transform: "rotate(180deg)" }}
                        aria-label={t("nav-left")}
                        disabled={!canScrollPrev}
                        onClick={() => handleScroll(SCROLL_DIRECTION.PREV)}
                    />
                    <ArrowButton aria-label={t("nav-right")} disabled={!canScrollNext} onClick={() => handleScroll(SCROLL_DIRECTION.NEXT)} />
                </Stack>
            </Container>
        </Box>
    );
};
